import html2canvas from 'html2canvas';
import { from, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { Component, HostBinding, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';
import { SnMenuItemConfig } from '@lib/ui/menu';
import { MediaMessageVaultsDialogComponent } from '@lib/vault';

import { SnChannel, SnChatUser, SnMessage } from '../../models';
import { SnChatService } from '../../services';
import { TelestrateDialogComponent } from '../dialogs/telestrate-dialog/telestrate-dialog.component';

@Component({
  selector: 'sn-message-item',
  templateUrl: './message-item.component.html',
  styleUrls: ['./message-item.component.scss'],
  host: { class: 'sn-message-item' },
})
export class SnMessageItemComponent implements OnInit, OnDestroy, OnChanges {
  @HostBinding('class.sn-message-item--selected')
  public isEditing = false;
  public isMedia: boolean;

  @Input()
  get message(): SnMessage {
    return this._message;
  }
  set message(value: SnMessage) {
    this._message = value;
    this.isMedia = value.twilio.type === 'media';
    this.isSystemMessage = this._message.twilio.author === 'system';
  }

  @Input()
  public author: SnChatUser;

  @Input()
  public inherited: boolean;

  @Input()
  get channel(): SnChannel {
    return this._channel;
  }
  set channel(value: SnChannel) {
    this._channel = value;
  }

  public readonly canSendToVault: boolean;

  public readonly menuActions: SnMenuItemConfig<SnMessage>[] = [
    {
      icon: {
        fontSet: 'fal',
        fontIcon: 'fa-pen',
      },
      label: t('common.edit'),
      click: () => (this.isEditing = true),
      hidden: (item) =>
        item.twilio.type === 'media' || (item.twilio.type !== 'text' && !this.isOwnMessage),
    },
    {
      icon: {
        fontSet: 'fal',
        fontIcon: 'fa-pen-nib',
      },
      label: t('communication.chat.actions.editImage'),
      click: () => this._editImage(),
      hidden: (item) => item.twilio.type !== 'media',
    },
    {
      icon: {
        fontSet: 'fal',
        fontIcon: 'fa-vault',
      },
      label: t('common.addToVault'),
      click: (item) => this._addToVault(item),
      hidden: () => !this.canSendToVault || !this.isMedia,
    },
    {
      label: t('common.delete'),
      icon: {
        fontSet: 'fal',
        fontIcon: 'fa-trash-alt',
      },
      click: (item) => this._deleteMessage(item),
      hidden: () => !this.isOwnMessage,
    },
  ];

  public isOwnMessage = false;
  public isSystemMessage = false;

  private _message: SnMessage;
  private _channel: SnChannel;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dialogService: SnDialogService,
    private readonly _chatService: SnChatService,
    private readonly _dialog: MatDialog,
    ability: SnAbilityService,
  ) {
    this.canSendToVault = ability.can({ subject: 'vaults/incidentmedia', action: 'add' });
  }

  public ngOnInit(): void {
    this.isOwnMessage = this.message.twilio.author === this._chatService.ownTwilioUser.identity;
  }

  public ngOnChanges(): void {
    if (this._channel.extMembers) {
      this._channel.extMembers.forEach((ext) => {
        if (ext.chatUser.id === this._message.twilio.author) {
          this._message.extMessage = true;
          this._message.organization = ext.organization;
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onChangesCancel(): void {
    this.isEditing = false;
  }

  public onChangesSave(value: string): void {
    if (value) {
      if (value.trim() !== this._message.twilio.body) {
        this._message.twilio.updateBody(value.trim());
        this.isEditing = false;
      }
    } else {
      this._deleteMessage(this._message);
    }
  }

  private _addToVault(message: SnMessage): void {
    this._dialog.open(MediaMessageVaultsDialogComponent, {
      data: message.mediaSource,
    });
  }

  private _deleteMessage(message: SnMessage): void {
    this._dialogService
      .confirm({
        message: t('communication.chat.confirmations.deleteMessage'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => from(message.twilio.remove())),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  private _editImage(): void {
    from(this.message.twilio.media.getContentTemporaryUrl())
      .pipe(takeUntil(this._destroy$))
      .subscribe((source: string) => {
        const img = document.createElement('img');
        img.src = source;
        img.crossOrigin = 'Anonymous';
        img.width = 800;
        img.height = 600;
        const body = document.querySelector('body');
        body.appendChild(img);
        html2canvas(img, {
          useCORS: true,
          allowTaint: true,
          ignoreElements: (node) => {
            return node.nodeName === 'IFRAME';
          },
        }).then((canvas: HTMLCanvasElement) => {
          this._telestrate(body, img, canvas);
        });
      });
  }

  private _telestrate(
    body: HTMLBodyElement,
    img: HTMLImageElement,
    canvas: HTMLCanvasElement,
  ): void {
    const dialogRef = this._dialog.open(TelestrateDialogComponent, {
      data: {
        channel: this._channel,
      },
      disableClose: true,
    });
    dialogRef.componentInstance.canvas = canvas;
    dialogRef.afterClosed().subscribe(() => {
      body.removeChild(img);
    });
  }
}
